import React, { useState, useEffect } from "react";

// hooks
import {
  useUpdateShipment,
  useCompleteShipment,
  useCancelShipment,
  useShipmentDetails,
  useVoidShippingLabels,
  useShipmentPickSheet,
  useUpdateItemStatus,
} from "../../../../../hooks/api/fulfillment/shipment";
import { useAppointmentUpdateServiceLevel } from "hooks/api/logistics/appointment";
import { useCancelPackageDelivery, useRedeliverPackage } from "hooks/api/logistics/shipment";
import { useQuery, useMutation } from "react-query";

// components
import {
  ShipmentCard as FulfillmentShipmentCard,
  PackageIssueDetailsModal,
  ProofOfDeliveryModal,
} from "@secondcloset/web-components";
import MarkAsIncompleteModal from "../../../../../components/_common/MarkAsIncompleteModal";
import RescheduleShipmentModal from "./RescheduleShipmentModal";
import NotesModal from "./NotesModal";
import FieldOpsNotesModal from "./FieldOpsNotesModal";
import { RecreateShipmentModal } from "./RecreateShipmentModal";
import ProcessInventoryReceivingItemsModal from "./ProcessInventoryReceivingItemsModal";
import PackageSummaryModal from "./PackageSummaryModal";
import UploadBillOfLadingModal from "./UploadBillOfLadingModal";
import IssueDetailsModal from "./IssueDetailsModal";
import { notification, message, Button } from "antd";

// libs
import Organization from "../../../../../lib/organization";
import * as shipmentCardLib from "./helper";
import { objectToFormData } from "object-to-formdata";
import Cookies from "../../../../../lib/Cookies";
import { isSuperAdmin, isSupportEscalated } from "../../../../../helperFunctions";

// api
import {
  createPickSheet,
  fetchShipmentItems,
  updateShipmentMethod,
  updateShipmentStatus as apiUpdateShipmentStatus,
} from "../../../../../api/fulfillment/shipment";

import { updateShipment } from "../../../../../api/logistics/shipment";

// styles
import { Container } from "./styles";

// types
import { Fulfillment, Warehouse } from "@secondcloset/types";
import { SecondClosetShippingMethod } from "@secondcloset/types/src/fulfillment";
import { DeliveryMethodUpdateBody } from "@secondcloset/web-components/dist/components/fulfillment/ShipmentCard/ShipmentCard";
import { useFeaturePreviewStore } from "../../../../../components/FeaturePreviewModal";
import { refreshWorkOrder } from "../../../../../api/fulfillment/workOrder";
import { SyncOutlined } from "@ant-design/icons";
import { usePackageToggle } from "../../../../../hooks/usePackagesToggle";
import { Facility } from "../../../../../api/common/facility";
import { fetchOutboundWorkOrders } from "../../../../../api/novemberApi";

type Shipment = Fulfillment.Shipment;
type Order = Fulfillment.Order;
type ExternalCarrierShippingMethod = Fulfillment.ExternalCarrierShippingMethod;

interface ShipmentCardProps {
  shipment: Shipment;
  order: Order;
  facilities: Facility[];
  workOrders?: Record<string, Record<string, string>>;
  isLoading: boolean;
  userHasResetShipmentPermission: boolean;
  onRefetch: () => any;
}

const ShipmentCard: React.FC<ShipmentCardProps> = ({
  shipment,
  order,
  userHasResetShipmentPermission,
  onRefetch,
  isLoading,
  facilities,
  workOrders = {},
}) => {
  const [markAsIncompleteModalVisible, setMarkAsIncompleteModalVisible] = useState(false);
  const [activeShipment, setActiveShipment] = useState<Fulfillment.Shipment<"V2">>(shipment); // TODO FIX SHIPMENT CARD SUPPORT FOR V1 and V2
  const [rescheduleShipmentModalVisible, setRescheduleShipmentModalVisible] = useState(false);
  const [recreateShipmentModalVisible, setRecreateShipmentModalVisible] = useState(false);
  const [notesModalVisible, setNotesModalVisible] = useState(false);
  const [fieldOpsNotesModalVisible, setFieldOpsNotesModalVisible] = useState(false);
  const [processInventoryReceivingItemsModalVisible, setProcessInventoryReceivingItemsModalVisible] = useState(false);
  const [packageSummaryModalVisible, setPackageSummaryModalVisible] = useState(false);
  const [uploadBOLModalVisible, setUploadBOLModalVisible] = useState(false);
  const [issueDetailsModalVisible, setIssueDetailsModalVisible] = useState(false);
  const [packageIssueDetailsModalVisible, setPackageIssueDetailsModalVisible] = useState(false);
  const [packageWithIssue, setPackageWithIssue] = useState("");
  const [proofOfDeliveryModalVisible, setProofOfDeliveryModalVisible] = useState(false);
  const [verificationImageIndex, setVerificationImageIndex] = useState(0);
  const [deletePackageLoading, setDeletePackageLoading] = useState<{
    [packageID: string]: boolean;
  }>({});
  const [isReturnToSender, setIsReturnToSender] = useState(false);
  const [returnToSenderAddressID, setReturnToSenderAddressID] = useState<string>();
  const [isBolCreated, setIsBolCreated] = useState(false);
  const { cancelShipmentStatus, cancelShipment } = useCancelShipment();
  const { updateShipmentStatus: updatedShipment, deleteFreightPackage } = useUpdateShipment();
  const { updateItemStatusData, updateItemStatus } = useUpdateItemStatus();
  const { completeShipmentStatus, completeShipment } = useCompleteShipment();
  const { shipmentDetails, updateShipmentStatus } = useShipmentDetails({ version: 1 });
  const { pickSheet, downloadPickSheetFile, downloadPickSheetFileV2 } = useShipmentPickSheet();
  const { voidShippingLabelsStatus, voidShippingLabels } = useVoidShippingLabels();
  const packageToggle = usePackageToggle();

  const { feature } = useFeaturePreviewStore();
  const shipmentItems = useQuery(["shipmentItems", shipment.id], () => fetchShipmentItems(shipment.id), {
    enabled: !!shipment.id && feature.largeOrder,
  });

  const { mutateAsync: doUpdateShipment, isLoading: isUpdating } = useMutation(updateShipment, {
    onSuccess: onRefetch,
  });

  const { mutateAsync: doCreatePickSheet, ...pushPickSheet } = useMutation(createPickSheet, {
    onSuccess: onRefetch,
  });

  const { mutateAsync: doUpdateShipmentStatus, isLoading: isLoadingShipmentStatus } = useMutation(
    apiUpdateShipmentStatus,
    {
      onSuccess: onRefetch,
    }
  );

  const { mutate: doRefreshWorkOrder, ...refreshWorkOrderMutation } = useMutation(refreshWorkOrder, {
    onSuccess: onRefetch,
  });

  const outboundWorkOrdersQuery = useQuery(
    ["outboundWorkOrders", shipment.id],
    () => fetchOutboundWorkOrders([shipment.shipment_number]),
    {
      enabled: !!shipment.id && shipment.shipping_method.status == "shipment_created",
      select(data) {
        return data.reduce((acc: Record<string, string>, workOrder: any) => {
          acc[workOrder.shipment_number] = workOrder.id;
          return acc;
        }, {});
      },
    }
  );

  const onShipmentMethodUpdateSuccess = (shipment: Shipment, variables: Record<string, unknown>) => {
    message.success("Shipment method updated.");
    if (variables.isPrintPickSheet) downloadPickSheetFile([shipment.shipment_number]);
    if (shipment.shipping_method_type !== "appointment") onRefetch();
    if (shipment.shipping_method_type === "appointment") {
      setActiveShipment(shipment);
      setRescheduleShipmentModalVisible(true);
    }
  };

  const { mutate: onUpdateShipmentMethod, ...updatedShipmentMethod } = useMutation(updateShipmentMethod, {
    onSuccess: onShipmentMethodUpdateSuccess,
  });

  const error =
    updatedShipment.error ||
    cancelShipmentStatus.error ||
    completeShipmentStatus.error ||
    cancelShipmentStatus.error ||
    shipmentDetails.error ||
    voidShippingLabelsStatus.error ||
    updatedShipmentMethod.error ||
    updateItemStatusData.error;

  const handleFetchShipmentProductsTracking = async () => {
    return shipmentItems.data?.flatMap((i) =>
      i.tracking_info.map((t) => ({
        product_sku: i.product.sku,
        product_upc: i.product.upc,
        lot_number: t.lot_number,
        serial_number: t.serial_number,
        quantity: t.quantity,
      }))
    ) as unknown as Warehouse.ShipmentProductsTracking[];
  };

  const isEndy = Organization.isEndy(order?.organization);
  const isIkea = Organization.isIKEA(order?.organization);
  const user = Cookies.getUserInfo();

  useEffect(() => {
    if (error) notification.error({ message: error as string });
  }, [error]);

  const renderMarkAsIncompleteModal = () => {
    return (
      <MarkAsIncompleteModal
        shipment={shipment}
        visible={markAsIncompleteModalVisible}
        setVisible={setMarkAsIncompleteModalVisible}
        onRefetch={onRefetch}
      />
    );
  };

  const onRescheduleShipmentCancel = () => {
    setRescheduleShipmentModalVisible(false);
    onRefetch();
  };

  const renderRescheduleShipmentModal = () => {
    return (
      <RescheduleShipmentModal
        shipment={activeShipment}
        order={order}
        visible={rescheduleShipmentModalVisible}
        onCancel={() => onRescheduleShipmentCancel()}
        onRefetch={onRefetch}
        setActiveShipment={setActiveShipment}
      />
    );
  };

  const renderNotesModal = () => {
    return (
      <NotesModal
        shipment={shipment}
        visible={notesModalVisible}
        setVisible={setNotesModalVisible}
        onRefetch={onRefetch}
      />
    );
  };

  const renderFieldOpsNotesModal = () => {
    return (
      <FieldOpsNotesModal
        shipment={shipment}
        visible={fieldOpsNotesModalVisible}
        setVisible={setFieldOpsNotesModalVisible}
      ></FieldOpsNotesModal>
    );
  };

  const renderRecreateShipmentModal = () => {
    return (
      <RecreateShipmentModal
        visible={recreateShipmentModalVisible}
        setVisible={setRecreateShipmentModalVisible}
        shipment={shipment}
        order={order}
        onRefetch={onRefetch}
        isNewReturnToSender={isReturnToSender}
        returnToSenderAddressID={returnToSenderAddressID}
      />
    );
  };

  const renderProcessInventoryReceivingItemsModal = () => {
    return (
      <ProcessInventoryReceivingItemsModal
        shipment={shipment}
        shipmentItems={shipmentItems.data}
        visible={processInventoryReceivingItemsModalVisible}
        isLoading={!!isLoading || shipmentItems.isLoading}
        setVisible={setProcessInventoryReceivingItemsModalVisible}
        onRefetch={() => {
          onRefetch();
          shipmentItems.refetch();
        }}
      />
    );
  };

  const renderPackageSummaryModal = () => {
    return (
      <PackageSummaryModal
        packages={shipment?.packages || []}
        isVisible={packageSummaryModalVisible}
        onSubmit={() => {
          setPackageSummaryModalVisible(false);
          setIsBolCreated(true);
        }}
        onClose={() => setPackageSummaryModalVisible(false)}
      />
    );
  };

  const onBOLSubmit = async (file: File, bolNumber: string, trackingNumber?: string) => {
    const body = objectToFormData({
      bol_file: file,
      label_id: bolNumber.trim(),
      tracking_number: trackingNumber?.trim() || bolNumber.trim(),
    });
    await doUpdateShipment({ shipmentID: shipment.id, body });
    setUploadBOLModalVisible(false);
  };

  const renderUploadBOLModal = () => {
    return (
      <UploadBillOfLadingModal
        isVisible={uploadBOLModalVisible}
        onClose={() => setUploadBOLModalVisible(false)}
        onSubmit={onBOLSubmit}
        isLoading={updatedShipment.loading}
        shid={shipment?.shipment_number || "-"}
      />
    );
  };

  const renderIssueDetailsModal = () => {
    return (
      <IssueDetailsModal
        shipment={shipment}
        visible={issueDetailsModalVisible}
        setVisible={setIssueDetailsModalVisible}
        onRefetch={onRefetch}
      />
    );
  };

  const renderPackageIssueDetailsModal = () => {
    return (
      <PackageIssueDetailsModal
        shipment={shipment}
        packageId={packageWithIssue}
        visible={packageIssueDetailsModalVisible}
        setVisible={setPackageIssueDetailsModalVisible}
        isIkea={isIkea}
        isLoading={false}
      />
    );
  };

  const renderProofOfDeliveryModal = () => {
    return (
      <ProofOfDeliveryModal
        shipment={shipment}
        verificationImageIndex={verificationImageIndex}
        visible={proofOfDeliveryModalVisible}
        setVisible={setProofOfDeliveryModalVisible}
        setVerificationImageIndex={setVerificationImageIndex}
      />
    );
  };

  const updateStatusAndRefetch = async (status: string) => {
    const res = await updateShipmentStatus(shipment?.id || "", status);
    if (res) onRefetch();
  };

  const completeCPS = async (shipment: Shipment) => {
    const action = {
      completed: true,
      action: "customer_pickup",
    };
    const items = feature.largeOrder
      ? shipmentCardLib.customerPickupItemActionV2(shipmentItems.data || [], action)
      : shipmentCardLib.customerPickupItemAction(shipment.shipment_items || [], action);
    await updateItemStatus(shipment.id, items);
    await completeShipment(shipment);
    return await onRefetch();
  };

  const onCancelShipment = async () => {
    const isAppointment = shipment?.shipping_method_type === "appointment";
    if (!isAppointment) {
      const shippingMethod = shipment?.shipping_method as ExternalCarrierShippingMethod;
      const shippingLabels = shippingMethod?.shipping_labels || [];
      const allLabelsVoided = shippingLabels.every((l) => l.voided);
      if (!allLabelsVoided)
        return notification.warn({
          message: "Please void all shipping labels",
        });
    }
    await cancelShipment(shipment?.id);
    onRefetch();
  };

  const goToProductDetailsPage = (productID: string) => {
    window.open(`/organizations/${order?.organization.id}/products/${productID}`);
  };

  const onDeletePackage = async (packageID: string) => {
    const packageLoading = { ...deletePackageLoading };
    packageLoading[packageID] = true;
    setDeletePackageLoading(packageLoading);
    if (shipment?.freight) {
      await deleteFreightPackage(shipment.id, packageID);
    } else {
      const package_ = shipment?.packages?.find((p) => p.id === packageID);
      const labelID = package_?.logistics_external_carrier_shipping_label_id;
      if (labelID) await voidShippingLabels(shipment.id, [labelID]);
    }
    packageLoading[packageID] = false;
    setDeletePackageLoading(packageLoading);
    onRefetch();
  };

  const onClickPackageIssueModal = (packageId: string) => {
    setPackageWithIssue(packageId);
    setPackageIssueDetailsModalVisible(true);
  };

  const isMarkAsCompletedButtonDisabled = () => {
    if (shipmentCardLib.isInventoryWarehouseReceiving(shipment)) return shipmentCardLib.hasAnyIdleItem(shipment);
    const isExternalCarrierShipment = shipment?.shipping_method_type === "external_carrier_shipment";
    const isOutbound = shipment?.action_summary?.includes("delivery");
    const notWithCarrier = shipment?.shipping_method?.status !== "with_carrier";
    return isOutbound && notWithCarrier && isExternalCarrierShipment;
  };

  const onBOLButtonClick = async () => {
    const shippingMethod = shipment?.shipping_method as ExternalCarrierShippingMethod;
    const shippingLabel = shippingMethod?.shipping_labels?.find((s) => !s.voided);
    const isBOLValid = shippingLabel?.label_id && !shippingLabel?.voided;

    if (isBOLValid) {
      window.open(shippingLabel?.label_download, "_blank");
      await completeShipment(shipment, shipmentItems.data);
      return onRefetch();
    }
    if (isBolCreated) return setUploadBOLModalVisible(true);
    window.open("https://app.freightcom.com", "_blank");
    setPackageSummaryModalVisible(true);
  };

  const [updateServiceLevelMutation, updateServiceLevelResult] = useAppointmentUpdateServiceLevel();

  const updateServiceLevelObject = {
    onUpdate: async (appointmentID: string, serviceLevel: string) => {
      const data = {
        appointmentID,
        body: {
          delivery_service_level: serviceLevel,
        },
      };
      await updateServiceLevelMutation(data);
      onRefetch();
    },
    loading: updateServiceLevelResult.status === "loading",
  };

  const onUpdateShippingMethod = (body: DeliveryMethodUpdateBody, isPrintPickSheet?: boolean) =>
    onUpdateShipmentMethod({
      shipmentID: shipment.id,
      body,
      isPrintPickSheet: !!isPrintPickSheet,
    });
  const [cancelPackageDeliveryMutation, cancelPackageDeliveryResult] = useCancelPackageDelivery(shipment.id);

  const cancelPackageDelivery = {
    onUpdate: async (packageID: string) => {
      await cancelPackageDeliveryMutation(packageID);
      onRefetch();
    },
    loading: cancelPackageDeliveryResult.status === "loading",
  };

  const [redeliverPackageMutation, redeliverPackageResult] = useRedeliverPackage(shipment.id);

  const redeliverPackage = {
    onUpdate: async (packageID: string) => {
      await redeliverPackageMutation(packageID);
      onRefetch();
    },
    loading: redeliverPackageResult.status === "loading",
  };

  const getViewInBvrProps = () => {
    const { shipping_method } = shipment;

    if ("route_id" in shipping_method && shipping_method.route_id) {
      const { route_id } = shipping_method;

      return {
        onButtonClick: () => {
          window.open(`${process.env.REACT_APP_BVR_URL}/routing/route/${route_id}`, "_blank");
        },
      };
    }

    return undefined;
  };

  const renderRefreshStatus = (key: string) => {
    const workOrder = workOrders?.[key]?.work_order_id;
    if (!workOrder || shipment.shipping_method.status === "with_carrier") return;
    return (
      <Button
        onClick={() => doRefreshWorkOrder(workOrder)}
        type="text"
        icon={<SyncOutlined />}
        title="Refresh Status"
        loading={refreshWorkOrderMutation.isLoading}
      />
    );
  };

  return (
    <Container>
      <FulfillmentShipmentCard
        isSuperAdmin={isSuperAdmin(user?.role, user?.subrole)}
        isSupportEscalated={isSupportEscalated(user?.role, user?.subrole)}
        isIkea={isIkea}
        isLoading={isLoading || shipmentItems.isLoading}
        shipment={shipment}
        shipmentItems={shipmentItems.data}
        order={order}
        pushPickSheetButtonProps={{
          onClick: () => doCreatePickSheet({ shipmentID: shipment.id }),
          loading: pushPickSheet.isLoading,
          disabled: !!outboundWorkOrdersQuery.data?.[shipment.shipment_number],
        }}
        fetchShipmentProductTrackings={handleFetchShipmentProductsTracking}
        uploadBOLButtonProps={{
          onClick: onBOLButtonClick,
          loading: completeShipmentStatus.loading || isUpdating,
          // disabled the button until all shipment items are packed
          disabled: shipment?.shipping_method?.status === "shipment_created",
        }}
        updateServiceLevel={updateServiceLevelObject}
        isBolCreated={isBolCreated}
        markAsCompletedButtonProps={{
          onClick: async () => {
            if (shipment?.shipping_method_type === "customer_pickup_shipment") {
              await completeCPS(shipment);
            } else {
              await completeShipment(shipment);
              onRefetch();
            }
          },
          loading: completeShipmentStatus.loading,
          disabled: isMarkAsCompletedButtonDisabled(),
        }}
        viewIssueDetailsButtonProps={{
          onClick: () => setIssueDetailsModalVisible(true),
        }}
        markAsIncompleteButtonProps={{
          onClick: () => setMarkAsIncompleteModalVisible(true),
        }}
        cancelShipmentButtonProps={{
          onClick: onCancelShipment,
          loading: cancelShipmentStatus.loading,
        }}
        rescheduleButtonProps={{
          onClick: () => setRescheduleShipmentModalVisible(true),
        }}
        processItemsButtonProps={{
          onClick: () => setProcessInventoryReceivingItemsModalVisible(true),
        }}
        notesButtonProps={{
          onClick: () => setNotesModalVisible(true),
        }}
        fieldOpsNotesButtonProps={{
          onClick: () => setFieldOpsNotesModalVisible(true),
        }}
        isAdmin
        packItemsButtonProps={{
          onClick: () => {
            window.open(process.env.REACT_APP_PROVIDENCE_WEB + `packing-scan-shipment/${shipment?.id}`, "_blank");
          },
        }}
        markAsFailedButtonProps={{
          loading: shipmentDetails.loading,
          onClick: () => updateStatusAndRefetch("failed"),
        }}
        resumeAppointmentButtonProps={{
          loading: shipmentDetails.loading,
          onClick: () => updateStatusAndRefetch("arrive"),
        }}
        recreateShipmentButtonProps={{
          onClick: () => {
            setRecreateShipmentModalVisible(true);
            setIsReturnToSender(false);
          },
        }}
        returnToSenderButtonProps={{
          onClick: () => {
            /**
             * Need to find the original address ID for return to sender
             */
            const inventoryPickupShipment = order?.shipments?.find((s) => {
              const shippingMethod = s?.shipping_method as SecondClosetShippingMethod;
              return shippingMethod?.job_type === "inventory_pick_up";
            });
            const inventoryPickupAppointment = inventoryPickupShipment?.shipping_method as SecondClosetShippingMethod;
            const addressID = inventoryPickupAppointment?.location?.id;
            setRecreateShipmentModalVisible(true);
            setIsReturnToSender(true);
            setReturnToSenderAddressID(addressID);
          },
        }}
        cancelPackageButtonProps={{
          onClick: (packageId: string) => cancelPackageDelivery.onUpdate(packageId),
          loading: cancelPackageDelivery.loading,
        }}
        redeliverPackageButtonProps={{
          onClick: (packageId: string) => redeliverPackage.onUpdate(packageId),
          loading: redeliverPackage.loading,
        }}
        viewPackageIssueDetailsButtonProps={{
          onClick: (packageId: string) => onClickPackageIssueModal(packageId),
        }}
        onDeletePackage={onDeletePackage}
        deletePackageLoading={deletePackageLoading}
        onProductClick={goToProductDetailsPage}
        onVerificationImageClick={(index) => {
          setProofOfDeliveryModalVisible(true);
          setVerificationImageIndex(index);
        }}
        printPickSheetButtonProps={{
          onClick: () => downloadPickSheetFileV2([shipment.id]),
          loading: pickSheet.loading,
        }}
        isEndy={isEndy}
        onUpdateShippingMethod={onUpdateShippingMethod}
        isDeliveryMethodLoading={updatedShipmentMethod.isLoading || pickSheet.loading}
        updateShipmentStatus={{
          onUpdate: (value: string) => updateStatusAndRefetch(value),
          loading: shipmentDetails.loading,
        }}
        onUpdateFulfilledFrom={(facilityID) =>
          doUpdateShipment({
            shipmentID: shipment.id,
            body: { facility_id: facilityID },
          })
        }
        userHasResetShipmentPermission={userHasResetShipmentPermission}
        resetAppointmentButtonProps={{
          onClick: () => doUpdateShipmentStatus({ shipmentID: shipment.id, status: "confirmed" }),
          loading: isLoadingShipmentStatus,
        }}
        viewRouteInBvr={getViewInBvrProps()}
        refreshShipmentStatusButton={renderRefreshStatus(shipment.shipment_number)}
        facilities={facilities}
        showPackages={packageToggle.value}
        onShowPackagesClick={packageToggle.toggle}
      />
      {renderMarkAsIncompleteModal()}
      {renderRescheduleShipmentModal()}
      {renderNotesModal()}
      {renderFieldOpsNotesModal()}
      {renderRecreateShipmentModal()}
      {renderProcessInventoryReceivingItemsModal()}
      {renderPackageSummaryModal()}
      {renderUploadBOLModal()}
      {renderIssueDetailsModal()}
      {renderPackageIssueDetailsModal()}
      {renderProofOfDeliveryModal()}
    </Container>
  );
};

export { ShipmentCard };
